import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FaStore, FaChartLine, FaTags, FaPercent, FaCalendarAlt, 
  FaTrash, FaFilter, FaSearch, FaSortAmountDown, 
  FaUpload, FaStar, FaStarHalfAlt, FaEye, FaMousePointer,
  FaShoppingCart, FaMoneyBillWave, FaHandshake, FaExclamationCircle,
  FaExternalLinkAlt
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import './MyMarketplace.css';

const MyMarketplace = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [myProducts, setMyProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortOption, setSortOption] = useState('published_at-desc');
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  const dummyMyProducts = [
    {
      _id: 'dummy_prod_1', product_id: 'exp1001', title: 'Eco-Friendly Water Bottle (Experience)',
      images: [{ url: '/waterbottle.png' }],
      status: 'active', is_featured: true,
      price_range: { min: 19.99, max: 19.99, currency: 'EUR' },
      commission_rate: 15, available_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      categories: ['Sustainable', 'Drinkware'], description: 'A stylish and reusable water bottle made from recycled materials.',
      published_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      marketplace_stats: { views: 1250, clicks: 300, tiktoker_selections: 25, sales: 50, revenue: 999.50, commission_paid: 149.93 },
      other_marketplace_listings: [
        { marketplace_name: 'Amazon DE', status: 'active', quantity: 45, views: 2100, sales: 60, revenue: 1319.40, link: '#amazon-dummy', last_sync: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString() },
        { marketplace_name: 'Etsy Handmade', status: 'active', quantity: 15, views: 850, sales: 10, revenue: 199.90, link: '#etsy-dummy', last_sync: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString() }
      ]
    },
    {
      _id: 'dummy_prod_2', product_id: 'exp1002', title: 'Handmade Ceramic Mug Set (Experience)',
      images: [{ url: '/astromug.png' }],
      status: 'active', is_featured: false,
      price_range: { min: 35.00, max: 45.00, currency: 'EUR' },
      commission_rate: 20, available_until: null,
      categories: ['Home Goods', 'Kitchenware', 'Handmade'], description: 'Set of two unique, handcrafted ceramic mugs. Perfect for gifts.',
      published_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
      marketplace_stats: { views: 800, clicks: 150, tiktoker_selections: 10, sales: 20, revenue: 700.00, commission_paid: 140.00 },
      other_marketplace_listings: [
        { marketplace_name: 'TikTok Shop US', status: 'active', quantity: 100, views: 15000, sales: 250, revenue: 8750.00, link: '#tiktok-dummy', last_sync: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString() }
      ]
    },
    {
      _id: 'dummy_prod_3', product_id: 'exp1003', title: 'Organic Cotton T-Shirt (Experience)',
      images: [{ url: '/tshirtgalaxy.png' }],
      status: 'inactive', is_featured: false,
      price_range: { min: 25.00, max: 25.00, currency: 'EUR' },
      commission_rate: 10, available_until: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
      categories: ['Apparel', 'Organic'], description: 'Comfortable and soft t-shirt made from 100% organic cotton.',
      published_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      marketplace_stats: { views: 450, clicks: 80, tiktoker_selections: 5, sales: 12, revenue: 300.00, commission_paid: 30.00 },
    }
  ];
  const dummyCategories = ['Sustainable', 'Drinkware', 'Home Goods', 'Kitchenware', 'Handmade', 'Apparel', 'Organic', 'Gadgets', 'Tech'];

  const fetchUserData = async (token) => {
    try {
      const response = await axios.get('/api/users/user', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      if (error.response && (error.response.status === 401 || error.response.status === 422)) {
        sessionStorage.removeItem('access_token');
        navigate('/');
      }
      return null;
    }
  };

  const fetchRealMyMarketplaceProducts = async (token) => {
    try {
      const response = await axios.get(
        '/api/shopify/my/marketplace/products',
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      const allCategories = response.data.products
        .flatMap(product => product.categories || [])
        .filter((category, index, self) => self.indexOf(category) === index);
      
      setMyProducts(response.data.products);
      setCategories(allCategories);
    } catch (err) {
      console.error('Failed to fetch real marketplace products:', err);
      setError(err.message);
       if (err.response && (err.response.status === 401 || err.response.status === 422)) {
        sessionStorage.removeItem('access_token');
        navigate('/');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      const token = sessionStorage.getItem('access_token');

      if (!token) {
        navigate('/');
        setIsLoading(false);
        return;
      }

      const fetchedUser = await fetchUserData(token);
      setUser(fetchedUser);

      if (fetchedUser && fetchedUser.role === 'experience') {
        setMyProducts(dummyMyProducts);
        setCategories(dummyCategories);
        setIsLoading(false);
        setError(null);
      } else if (fetchedUser) {
        await fetchRealMyMarketplaceProducts(token);
      } else {
        if (!error) setError(t('common.authorizationError'));
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [navigate, t]);

  const handleRemoveFromMarketplace = async (productId) => {
    if (!window.confirm(t('myMarketplace.confirmRemove'))) {
      return;
    }

    if (user && user.role === 'experience') {
      setMyProducts(prevProducts => prevProducts.filter(p => p.product_id !== productId));
      alert(t('myMarketplace.removeSuccess'));
      return;
    }

    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
          navigate('/');
          return;
      }
      
      await axios.delete(
        '/api/shopify/remove_from_marketplace',
        {
          headers: { Authorization: `Bearer ${token}` },
          data: {
            product_ids: [productId]
          }
        }
      );
      
      setMyProducts(prevProducts => prevProducts.filter(product => 
        product.product_id !== productId
      ));
      
      alert(t('myMarketplace.removeSuccess'));
    } catch (err) {
      console.error('Failed to remove product from marketplace:', err);
      setError(err.message);
       if (err.response && (err.response.status === 401 || err.response.status === 422)) {
        sessionStorage.removeItem('access_token');
        navigate('/');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('myMarketplace.noExpiration');
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return t('myMarketplace.notAvailable');
    return new Date(dateString).toLocaleString();
  };

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const filterProducts = () => {
    let filtered = myProducts;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(product => 
        product.title.toLowerCase().includes(term) ||
        (product.description && product.description.toLowerCase().includes(term))
      );
    }
    
    if (filterCategory !== 'all') {
      filtered = filtered.filter(product => 
        product.categories && product.categories.includes(filterCategory)
      );
    }
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(product => product.status === filterStatus);
    }
    
    const [field, direction] = sortOption.split('-');
    filtered = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (field) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'price':
          comparison = a.price_range.min - b.price_range.min;
          break;
        case 'commission':
          comparison = a.commission_rate - b.commission_rate;
          break;
        case 'published_at':
          comparison = new Date(a.published_at) - new Date(b.published_at);
          break;
        case 'views':
          comparison = a.marketplace_stats.views - b.marketplace_stats.views;
          break;
        case 'sales':
          comparison = a.marketplace_stats.sales - b.marketplace_stats.sales;
          break;
        case 'revenue':
          comparison = a.marketplace_stats.revenue - b.marketplace_stats.revenue;
          break;
        default:
          comparison = 0;
      }
      
      return direction === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  };

  const filteredProducts = filterProducts();

  if (error) return (
    <div className="una-marketplace-error">
      <FaExclamationCircle />
      <h2>{t('common.error')}</h2>
      <p>{error}</p>
    </div>
  );
  
  if (isLoading) {
    return (
      <div className="una-marketplace-loading-screen">
        <div className="una-loading-content">
          <div className="una-loading-logo">
            <img src="/bgless_logo.png" alt="Unanimity AI Logo" />
          </div>
          <h2>{t('myMarketplace.loadingTitle')}</h2>
          <div className="una-loading-progress">
            <div className="una-progress-bar">
              <div className="una-progress-glow"></div>
            </div>
          </div>
          <div className="una-loading-metrics">
            <div className="una-metric-item">
              <FaStore />
              <span>{t('myMarketplace.fetchingProducts')}</span>
            </div>
            <div className="una-metric-item">
              <FaChartLine />
              <span>{t('myMarketplace.loadingStats')}</span>
            </div>
            <div className="una-metric-item">
              <FaHandshake />
              <span>{t('myMarketplace.processingData')}</span>
            </div>
            <div className="una-metric-item">
              <FaMoneyBillWave />
              <span>{t('myMarketplace.calculatingRevenue')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="una-my-marketplace-container">
      <div className="una-marketplace-content">
        <div className="una-marketplace-filters">
          <div className="una-search-group">
            <FaSearch className="una-search-icon" />
            <input
              type="text"
              className="una-search-input"
              placeholder={t('myMarketplace.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="una-filter-group">
            <label className="una-filter-label">
              <FaFilter /> {t('myMarketplace.categoryFilter')}
            </label>
            <select 
              className="una-filter-select"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <option value="all">{t('myMarketplace.allCategories')}</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          
          <div className="una-filter-group">
            <label className="una-filter-label">
              <FaFilter /> {t('myMarketplace.statusFilter')}
            </label>
            <select 
              className="una-filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">{t('myMarketplace.allStatuses')}</option>
              <option value="active">{t('myMarketplace.statusActive')}</option>
              <option value="inactive">{t('myMarketplace.statusInactive')}</option>
            </select>
          </div>
          
          <div className="una-filter-group">
            <label className="una-filter-label">
              <FaSortAmountDown /> {t('myMarketplace.sort')}
            </label>
            <select 
              className="una-filter-select"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
            >
              <option value="published_at-desc">{t('myMarketplace.sortNewest')}</option>
              <option value="published_at-asc">{t('myMarketplace.sortOldest')}</option>
              <option value="title-asc">{t('myMarketplace.sortTitleAZ')}</option>
              <option value="title-desc">{t('myMarketplace.sortTitleZA')}</option>
              <option value="price-asc">{t('myMarketplace.sortPriceLow')}</option>
              <option value="price-desc">{t('myMarketplace.sortPriceHigh')}</option>
              <option value="commission-desc">{t('myMarketplace.sortCommissionHigh')}</option>
              <option value="views-desc">{t('myMarketplace.sortMostViews')}</option>
              <option value="sales-desc">{t('myMarketplace.sortMostSales')}</option>
              <option value="revenue-desc">{t('myMarketplace.sortMostRevenue')}</option>
            </select>
          </div>
        </div>

        <div className="una-marketplace-stats-summary">
          <div className="una-stat-card">
            <div className="una-stat-icon"><FaEye /></div>
            <div className="una-stat-content">
              <div className="una-stat-value">
                {formatNumber(
                  myProducts.reduce((sum, product) => {
                    let productViews = product.marketplace_stats?.views || 0;
                    if (user && user.role === 'experience' && product.other_marketplace_listings) {
                      productViews += product.other_marketplace_listings.reduce((otherSum, listing) => otherSum + (listing.views || 0), 0);
                    }
                    return sum + productViews;
                  }, 0)
                )}
              </div>
              <div className="una-stat-label">{t('myMarketplace.totalViews')}</div>
            </div>
          </div>
          
          <div className="una-stat-card">
            <div className="una-stat-icon"><FaHandshake /></div>
            <div className="una-stat-content">
              <div className="una-stat-value">
                {formatNumber(myProducts.reduce((sum, product) => sum + product.marketplace_stats.tiktoker_selections, 0))}
              </div>
              <div className="una-stat-label">{t('myMarketplace.tiktokerSelections')}</div>
            </div>
          </div>
          
          <div className="una-stat-card">
            <div className="una-stat-icon"><FaShoppingCart /></div>
            <div className="una-stat-content">
              <div className="una-stat-value">
                {formatNumber(myProducts.reduce((sum, product) => sum + product.marketplace_stats.sales, 0))}
              </div>
              <div className="una-stat-label">{t('myMarketplace.totalSales')}</div>
            </div>
          </div>
          
          <div className="una-stat-card">
            <div className="una-stat-icon"><FaMoneyBillWave /></div>
            <div className="una-stat-content">
              <div className="una-stat-value">
                {formatCurrency(myProducts.reduce((sum, product) => sum + product.marketplace_stats.revenue, 0))}
              </div>
              <div className="una-stat-label">{t('myMarketplace.totalRevenue')}</div>
            </div>
          </div>
        </div>

        {filteredProducts.length === 0 ? (
          <div className="una-empty-state">
            <FaStore />
            <p>{t('myMarketplace.noProducts')}</p>
            <Link to="/marketplace-listing" className="una-create-button">
              {t('myMarketplace.addProductsToMarketplace')}
            </Link>
          </div>
        ) : (
          <div className="una-marketplace-grid">
            {filteredProducts.map((product) => (
              <div key={product._id} className="una-marketplace-card">
                <div className="una-product-image-container">
                  {product.images && product.images.length > 0 ? (
                    <img 
                      src={product.images[0].url} 
                      alt={product.title} 
                      className="una-product-image" 
                    />
                  ) : (
                    <div className="una-product-no-image">
                      <FaStore />
                    </div>
                  )}
                  
                  {product.is_featured && (
                    <div className="una-featured-badge">
                      <FaStar /> {t('myMarketplace.featured')}
                    </div>
                  )}
                  
                  <div className={`una-status-badge ${product.status}`}>
                    {product.status === 'active' 
                      ? t('myMarketplace.statusActive') 
                      : t('myMarketplace.statusInactive')}
                  </div>
                </div>
                
                <div className="una-product-details">
                  <h4>{product.title}</h4>
                  
                  <div className="una-product-meta">
                    <p className="una-product-price">
                      <FaMoneyBillWave /> 
                      {product.price_range.min} {product.price_range.currency}
                      {product.price_range.min !== product.price_range.max && 
                        ` - ${product.price_range.max} ${product.price_range.currency}`}
                    </p>
                    
                    <p className="una-product-commission">
                      <FaPercent /> {t('myMarketplace.commission')}: {product.commission_rate}%
                    </p>
                    
                    {product.available_until && (
                      <p className="una-product-expiry">
                        <FaCalendarAlt /> {t('myMarketplace.availableUntil')}: {formatDate(product.available_until)}
                      </p>
                    )}
                  </div>
                  
                  <div className="una-product-categories">
                    {product.categories && product.categories.map(category => (
                      <span key={category} className="una-category-tag">
                        <FaTags /> {category}
                      </span>
                    ))}
                  </div>
                  
                  <div className="una-product-stats">
                    <h5><FaChartLine /> {t('myMarketplace.performanceMetrics')}</h5>
                    <div className="una-stats-grid">
                      <div className="una-stat-item">
                        <FaEye />
                        <span>{t('myMarketplace.views')}</span>
                        <strong>{formatNumber(product.marketplace_stats.views)}</strong>
                      </div>
                      <div className="una-stat-item">
                        <FaMousePointer />
                        <span>{t('myMarketplace.clicks')}</span>
                        <strong>{formatNumber(product.marketplace_stats.clicks)}</strong>
                      </div>
                      <div className="una-stat-item">
                        <FaHandshake />
                        <span>{t('myMarketplace.selected')}</span>
                        <strong>{formatNumber(product.marketplace_stats.tiktoker_selections)}</strong>
                      </div>
                      <div className="una-stat-item">
                        <FaShoppingCart />
                        <span>{t('myMarketplace.sales')}</span>
                        <strong>{formatNumber(product.marketplace_stats.sales)}</strong>
                      </div>
                      <div className="una-stat-item wider">
                        <FaMoneyBillWave />
                        <span>{t('myMarketplace.revenue')}</span>
                        <strong>{formatCurrency(product.marketplace_stats.revenue)}</strong>
                      </div>
                      <div className="una-stat-item wider">
                        <FaPercent />
                        <span>{t('myMarketplace.commissionPaid')}</span>
                        <strong>{formatCurrency(product.marketplace_stats.commission_paid)}</strong>
                      </div>
                    </div>
                  </div>

                  {/* Section for Other Marketplace Listings - Only for 'experience' user */}
                  {user && user.role === 'experience' && product.other_marketplace_listings && product.other_marketplace_listings.length > 0 && (
                    <div className="una-other-marketplaces-section">
                      <h5>{t('myMarketplace.otherListings', 'Other Marketplace Listings')}</h5>
                      {product.other_marketplace_listings.map((listing, index) => (
                        <div key={index} className="una-other-marketplace-item">
                          <div className="una-other-marketplace-header">
                            <strong>{listing.marketplace_name}</strong>
                            <span className={`una-status-badge ${listing.status.toLowerCase().replace(' ', '-')}`}>
                              {listing.status}
                            </span>
                          </div>
                          <div className="una-other-marketplace-stats">
                            <span><FaStore /> {t('myMarketplace.quantity', 'Qty')}: {listing.quantity}</span>
                            <span><FaEye /> {t('myMarketplace.views')}: {formatNumber(listing.views)}</span>
                            <span><FaShoppingCart /> {t('myMarketplace.sales')}: {formatNumber(listing.sales)}</span>
                            <span><FaMoneyBillWave /> {t('myMarketplace.revenue')}: {formatCurrency(listing.revenue)}</span>
                          </div>
                           <div className="una-other-marketplace-footer">
                             <span>{t('myMarketplace.lastSync', 'Last Sync')}: {formatDateTime(listing.last_sync)}</span>
                            {listing.link && (
                              <a href={listing.link} target="_blank" rel="noopener noreferrer" className="una-external-link-button">
                                <FaExternalLinkAlt /> {t('myMarketplace.viewListing', 'View Listing')}
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <button
                    onClick={() => handleRemoveFromMarketplace(product.product_id)}
                    className="una-remove-button"
                  >
                    <FaTrash /> {t('myMarketplace.removeFromMarketplace')}
                  </button>
                </div>
              </div>
            ))}
            <Link to="/marketplace-listing" className="una-marketplace-add-card">
              <FaUpload />
              <span>{t('myMarketplace.goToMarketplace')}</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyMarketplace;